import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Calendar, ArrowLeft, User, FileText, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import DOMPurify from 'dompurify';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const BlogDetailPage = () => {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlog();
  }, [id]);

  const fetchBlog = async () => {
    try {
      const response = await axios.get(`${API_URL}/blogs/${id}`);
      setBlog(response.data);
    } catch (error) {
      console.error('Failed to fetch blog:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      {loading ? (
        <div className="flex-1 flex items-center justify-center py-32">
          <div className="text-center">
            <div className="inline-block w-8 h-8 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
            <p className="mt-4 text-muted-foreground">載入中...</p>
          </div>
        </div>
      ) : blog ? (
        <>
          {/* Hero header */}
          <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-500 to-blue-700 pt-16 pb-24">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-96 h-96 bg-yellow-300 rounded-full blur-3xl" />
            </div>
            <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
              <Button asChild variant="ghost" className="mb-6 text-white/80 hover:text-white hover:bg-white/10 -ml-3">
                <Link to="/blog">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  返回考試資料
                </Link>
              </Button>

              <div className="flex items-center gap-3 mb-5">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/15 backdrop-blur-sm text-white text-xs font-semibold rounded-md">
                  <FileText className="h-3 w-3" />
                  {blog.category}
                </span>
                <span className="flex items-center gap-1.5 text-sm text-blue-100">
                  <Calendar className="h-3.5 w-3.5" />
                  {format(new Date(blog.created_at), 'yyyy年 MM月 dd日')}
                </span>
                <span className="flex items-center gap-1.5 text-sm text-blue-100">
                  <User className="h-3.5 w-3.5" />
                  {blog.author}
                </span>
              </div>

              <h1 className="text-3xl md:text-5xl font-extrabold text-white leading-tight tracking-tight">
                {blog.title}
              </h1>
            </div>
          </section>

          {/* Content */}
          <section className="relative -mt-12 pb-20">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
              <article className="bg-white rounded-2xl shadow-lg border border-gray-100 px-8 py-10 md:px-12 md:py-14">
                <div
                  className="prose prose-lg prose-blue max-w-none
                    prose-headings:font-bold prose-headings:text-gray-900 prose-headings:tracking-tight
                    prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4 prose-h3:pb-2 prose-h3:border-b prose-h3:border-blue-100
                    prose-h4:text-lg prose-h4:mt-6 prose-h4:mb-3 prose-h4:text-blue-700
                    prose-p:text-gray-700 prose-p:leading-relaxed
                    prose-li:text-gray-700 prose-li:leading-relaxed
                    prose-ul:my-4 prose-ul:space-y-1
                    prose-a:text-blue-600 prose-a:font-semibold prose-a:no-underline hover:prose-a:underline
                    prose-strong:text-gray-900"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(blog.content, { ADD_ATTR: ['target'] }) }}
                />
              </article>

              {/* External link */}
              {blog.external_link && (
                <div className="mt-8 p-5 bg-blue-50 border border-blue-100 rounded-xl flex items-center justify-between gap-4">
                  <div>
                    <p className="text-xs text-blue-500 font-medium mb-0.5">官方連結</p>
                    <p className="text-sm font-semibold text-blue-800">
                      {blog.external_link_label || '運輸署官方網站'}
                    </p>
                  </div>
                  <a
                    href={blog.external_link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button size="sm" className="rounded-full gap-1.5 whitespace-nowrap">
                      立即前往
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Button>
                  </a>
                </div>
              )}

              {/* Bottom nav */}
              <div className="mt-8 text-center">
                <Button asChild variant="outline" size="lg" className="rounded-full px-8 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200">
                  <Link to="/blog">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    返回考試資料
                  </Link>
                </Button>
              </div>
            </div>
          </section>
        </>
      ) : (
        <div className="flex-1 flex items-center justify-center py-32">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
              <FileText className="h-8 w-8 text-blue-400" />
            </div>
            <p className="text-lg font-medium text-gray-700 mb-4">找不到文章</p>
            <Button asChild className="rounded-full">
              <Link to="/blog">返回考試資料</Link>
            </Button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default BlogDetailPage;
